import { memo, ReactNode } from 'react';

import Conversion from 'bloko/blocks/conversion';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';
import NumberFormatter from 'bloko/common/numberFormatter';
import { formatToReactComponent } from 'bloko/common/trl';

import translation from 'src/components/translation';
import { TariffProductType } from 'src/models/price/product.types';
import { getContactsCount, isUnlimitedResumeAccess } from 'src/utils/price/products';

export enum ContactsCountVariants {
    Compact = 'compact',
    Detailed = 'detailed',
}

const TrlKeys = {
    unlimitedContacts: 'price.contactsCount.unlimited',
    [ContactsCountVariants.Compact]: {
        one: 'price.contactsCount.one',
        some: 'price.contactsCount.some',
        many: 'price.contactsCount.many',
    },
    [ContactsCountVariants.Detailed]: {
        one: 'price.contactsCount.detailed.one',
        some: 'price.contactsCount.detailed.some',
        many: 'price.contactsCount.detailed.many',
    },
};

interface RawContactsCountProps {
    /** Количество контактов */
    count: number;
    /** Признак того, что указанное количество контактов считается безлимитным */
    isUnlimited: boolean;
    /** Вариант записи */
    variant: ContactsCountVariants;
}

const RawContactsCount: TranslatedComponent<RawContactsCountProps> = ({ count, isUnlimited, variant, trls }) => {
    if (!count) {
        return null;
    }
    if (isUnlimited) {
        return <span data-qa="contacts-unlimited">{trls[TrlKeys.unlimitedContacts]}</span>;
    }
    return (
        <Conversion
            one={trls[TrlKeys[variant].one]}
            some={trls[TrlKeys[variant].some]}
            many={trls[TrlKeys[variant].many]}
            format={(trl) =>
                formatToReactComponent(trl, {
                    '{0}': <span data-qa="contacts-count">{NumberFormatter.format(String(count))}</span>,
                })
            }
            value={count}
            hasValue={false}
        />
    );
};

const ContactsCount = memo(translation(RawContactsCount));

interface UseContactsCountParams {
    /** Выводить точное количество контактов даже в безлимитных тарифах */
    noUnlimited?: boolean;
    /** Вариант записи */
    variant?: ContactsCountVariants;
}

/** 100 контактов резюме / Сколько угодно резюме */
const useContactsCount = (
    product: TariffProductType | null,
    { noUnlimited = false, variant = ContactsCountVariants.Compact }: UseContactsCountParams = {}
): ReactNode => {
    const isUnlimited = noUnlimited ? false : isUnlimitedResumeAccess(product);
    const contactsCount = product ? getContactsCount(product) : null;
    return contactsCount ? <ContactsCount count={contactsCount} isUnlimited={isUnlimited} variant={variant} /> : null;
};

export default useContactsCount;
